import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useSpendSummaryReportVisible } from 'shared/lib/hooks/useSpendSummaryReportVisible';
import { toNumber } from 'shared/lib/number/number';
import { useSpendSummaryDrillInData } from 'widgets/SpendSummaryReport/model/hooks/useSpendSummaryDrillInData';

import type {
    SpendCategoryRowDto,
    SpendInsightsSummaryDto,
    SpendProductsResponseDto,
} from 'entities/ReportSpendSummary';
import type { SpendDrillInLocationState } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

interface UseReportsPageSpendSummarySupplierResult {
    isSpendSummaryVisible: boolean | undefined;
    supplierId: string | undefined;
    supplierName: string;
    loading: boolean;
    summary: SpendInsightsSummaryDto | null;
    byCategory: SpendCategoryRowDto[];
    products: SpendProductsResponseDto | null;
    totalSpend: number;
    totalSpendPercent: number;
    orderCount: number;
    productsCount: number;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
    locationState: SpendDrillInLocationState;
}

export const useReportsPageSpendSummarySupplier =
    (): UseReportsPageSpendSummarySupplierResult => {
        const { supplierId } = useParams<{ supplierId: string }>();
        const location = useLocation();
        const isSpendSummaryVisible = useSpendSummaryReportVisible();
        const locationState =
            (location.state as SpendDrillInLocationState | null) ?? {};
        const contextTotalSpend = toNumber(locationState.contextTotalSpend);

        const {
            loading,
            summary,
            byCategory,
            bySupplier,
            products,
            totalSpend,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        } = useSpendSummaryDrillInData({
            dimension: 'supplier',
            id: supplierId,
            scope: locationState.scope,
        });

        const supplierName = useMemo(() => {
            if (locationState.supplierName) {
                return locationState.supplierName;
            }

            const fromSupplier = bySupplier.find(
                (item) => item.supplierId === supplierId,
            )?.supplierName;

            if (fromSupplier) {
                return fromSupplier;
            }

            return supplierId ?? '';
        }, [bySupplier, locationState.supplierName, supplierId]);

        const totalSpendPercent =
            contextTotalSpend > 0 ? (totalSpend / contextTotalSpend) * 100 : 0;

        return {
            isSpendSummaryVisible,
            supplierId,
            supplierName,
            loading,
            summary,
            byCategory,
            products,
            totalSpend,
            totalSpendPercent,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
            locationState,
        };
    };
